import { useMemo } from 'react';

type GenericElement = {
  id: string | number;
  displayName: string;
  version?: string;
  [key: string]: any;
};

// Helper function to get the display name from various possible fields
const getDisplayName = (
  element: GenericElement,
  customField?: string,
): string => {
  if (customField && element[customField]) {
    return element[customField];
  }

  return (
    element.displayName || element.display_name || element.name || element.id
  );
};

const createNameMap = (
  results: GenericElement[],
  customField?: string,
): Record<string, number> => {
  return results.reduce((acc, r) => {
    const displayName = getDisplayName(r, customField);
    acc[displayName] = (acc[displayName] ?? 0) + 1;
    return acc;
  }, {} as Record<string, number>);
};

export const useNameWithVersionMap = (
  adaptedValues: GenericElement[],
  customField?: string,
) => {
  return useMemo(() => {
    const nameMap = createNameMap(adaptedValues, customField);

    return adaptedValues.reduce((acc, val) => {
      const displayName = getDisplayName(val, customField);
      const nameWithVersion =
        nameMap[displayName] > 1 && val.version
          ? `${displayName} v${val.version?.split('.')[0]}`
          : displayName;

      acc[val.id] = {
        ...val,
        nameWithVersion,
      };

      return acc;
    }, {} as Record<string | number, GenericElement & { nameWithVersion: string }>);
  }, [adaptedValues, customField]);
};

export const createNameWithVersionMap = (
  adaptedValues: GenericElement[],
  customField?: string,
) => {
  const nameMap = createNameMap(adaptedValues, customField);

  return adaptedValues.reduce((acc, val) => {
    const displayName = getDisplayName(val, customField);
    const nameWithVersion =
      nameMap[displayName] > 1 && val.version
        ? `${displayName} v${val.version?.split('.')[0]}`
        : displayName;

    acc[val.id] = {
      ...val,
      nameWithVersion,
    };

    return acc;
  }, {} as Record<string | number, GenericElement & { nameWithVersion: string }>);
};
